import { CSSInterpolation } from '@emotion/css/create-instance'
import React, {
  type ChangeEvent,
  type FocusEvent,
  type MutableRefObject,
  type Ref,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import type { ReactInputProps } from '../../types'
import { IconButton } from './IconButton'
import { type InputState, InternalInput } from './internal/InternalInput'
import { InternalInputWrapper } from './internal/InternalInputWrapper'

type Props = {
  /**
   * Optional prop to change whether the value will be initially visible, defaults to false. This will only be used as a seed value (changes to this value will have no effect on the component behavior after it is mounted).
   */
  isValueInitiallyVisible?: boolean
  /**
   * Unformatted social security number (e.g. '123456789'). If empty, any provided `placeholder` is shown.
   */
  value: string
  /**
   * Callback that will be fired on blur of the input.
   */
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void
  /**
   * Callback called when the value in the input changes. As a convenience, the value is provided
   * separately from the change event for use cases that just need the new value. Note that the
   * value will be unformatted (e.g. '123456789').
   */
  onChange: ({
    event,
    value,
  }: {
    event: ChangeEvent<HTMLInputElement>
    value: string
  }) => void
  /**
   * Whether to disable the input or mark it as having an error.
   */
  state?: InputState
  /**
   * Provide `true` to visually hide the input label, while still leaving it present in the DOM
   * for screen readers. Ignored if no `label` is provided.
   */
  hideLabel?: boolean
  /**
   * A label to show above the input. If you would like to visually hide it please use
   * `hideLabel` prop to do so. If you do not provide a value here, you must provide your own
   * `<label>` element for the input for accessibility reasons.
   */
  label?: string
  /**
   * An error message to show below the input. Will only be shown if `state` is `"error"` and
   * this component also has a `label` specified. If another component is rendering the `label`
   * for this input, that component should be rendering the error message as well.
   */
  errorMessage?: string
  /**
   * A React ref to attach to the actual `input` element, e.g. for triggering focus or blur on
   * the input.
   */
  innerRef?:
    | Exclude<Ref<HTMLInputElement>, RefObject<HTMLInputElement | null>>
    | MutableRefObject<HTMLInputElement | null>
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
}

type DefaultInputProps = Omit<
  ReactInputProps,
  | keyof Props
  | 'disabled'
  | 'onChange'
  | 'css'
  | 'className'
  | 'style'
  | 'type'
  | 'onBlur'
  | 'value'
>

const MASKING_CHAR = '⦁'

function maskSSN(string: string) {
  return string.replace(/\d/g, MASKING_CHAR)
}

function formatSSN(value: string) {
  if (value.length >= 5) {
    return `${value.substring(0, 3)}-${value.substring(3, 5)}-${value.slice(5)}`
  }

  if (value.length >= 3) {
    return `${value.substring(0, 3)}-${value.slice(3)}`
  }

  return value
}

function ssnNormalizer(value: string, previousValue: string) {
  const cleanedValue = cleanSSNFormatting(value)

  const isTypingBackwards =
    previousValue != null && previousValue.length > value.length

  if (isTypingBackwards) {
    if (value.endsWith(' ') || value.endsWith('-')) {
      return value.replace(/\s?-?\s?$/, '')
    }

    // do not format again if we just removed a -
    if (cleanedValue.length !== 3 && cleanedValue.length !== 5) {
      return formatSSN(cleanedValue)
    }

    return value
  }

  return formatSSN(cleanedValue)
}

function cleanSSNFormatting(value: string) {
  return value.replace(/[^\d]/g, '')
}

/**
 * An input element for collecting Social Security Number (SSN) values.
 *
 * Like all inputs, this should be used inside a `<form>` element. You also may want to use
 * the `getInputState` helper.
 */
export function SSNInput({
  errorMessage,
  hideLabel = false,
  innerRef,
  isValueInitiallyVisible = false,
  label,
  onBlur: onBlurProp,
  onChange: onChangeProp,
  overrideRootStyles,
  state = 'default',
  // unformatted value
  value: valueProp,
  ...inputProps
}: Props & DefaultInputProps) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const previousSelectionStart = useRef<number | null>(null)
  const [isMasked, setIsMasked] = useState<boolean>(!isValueInitiallyVisible)

  // This is the value that is remembered before masking
  const [currentlyMaskedValue, setCurrentlyMaskedValue] = useState<
    string | null
  >(null)
  // This is the value internal to the component, keeps track of what's actually rendered
  const [displayValue, setDisplayValue] = useState(valueProp)
  // Note(auster.chen) since we also need a reference to the input in this implementation
  // we hack the `innerRef` pass through
  const setInputRef = useCallback(
    (element: HTMLInputElement | null) => {
      inputRef.current = element

      if (typeof innerRef === 'function') {
        innerRef(element)
      } else if (innerRef) {
        innerRef.current = element
      }
    },
    [innerRef]
  )

  const onChange = ({
    event,
    value,
  }: {
    value: string
    event: ChangeEvent<HTMLInputElement>
  }) => {
    // if we're editing the middle, maintain cursor position
    if ((inputRef.current?.selectionStart || 0) < value.length - 1) {
      previousSelectionStart.current =
        typeof inputRef.current?.selectionStart === 'number'
          ? inputRef.current?.selectionStart
          : 0
    } else {
      previousSelectionStart.current = null
    }

    const formattedValue = ssnNormalizer(value, displayValue)

    setDisplayValue(formattedValue)

    onChangeProp({
      event,
      value: cleanSSNFormatting(formattedValue),
    })

    // always provide unformatted value to consumer
    onChangeProp({ event, value: cleanSSNFormatting(value) })
  }

  const onToggleisMasked = () => {
    // current is mask on blur, switching to always visible
    if (isMasked) {
      setCurrentlyMaskedValue(null)
      setIsMasked(false)
    } else {
      setIsMasked(true)
      setCurrentlyMaskedValue(displayValue)
    }
  }

  const onBlur = (event: FocusEvent<HTMLInputElement>) => {
    // when blurring update the value with proper formatting
    const formattedValue = formatSSN(cleanSSNFormatting(displayValue))

    if (isMasked) {
      setDisplayValue(formattedValue)
      setCurrentlyMaskedValue(formattedValue)
    }

    onBlurProp?.(event)
  }

  useLayoutEffect(() => {
    if (previousSelectionStart.current !== null) {
      inputRef.current?.setSelectionRange(
        previousSelectionStart.current,
        previousSelectionStart.current
      )
    }
  }, [displayValue])

  useEffect(() => {
    const newdisplayValue = formatSSN(valueProp)

    setDisplayValue(newdisplayValue)

    if (currentlyMaskedValue != null) {
      setCurrentlyMaskedValue(newdisplayValue)
    }
  }, [valueProp, isMasked, currentlyMaskedValue])

  useEffect(() => {
    if (isMasked) {
      setCurrentlyMaskedValue(displayValue)
    }
    // Note (@auster.chen) explicitly run this only on first time this components mounts as we
    // won't have a blur effect if there is initial value present.
    // NOTE (david.aghassi) this was disabled during the upgrade of eslint-plugin-react-hooks.
    // Or was a pre-existing offense
    // Please refactor and solve the issue next time this code is touched
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <InternalInputWrapper
      errorMessage={errorMessage}
      hideLabel={hideLabel}
      label={label}
      overrideRootStyles={overrideRootStyles}
      state={state}
    >
      {errorMessageId => (
        <InternalInput
          aria-describedby={errorMessageId}
          autoComplete="off"
          endControl={
            <IconButton
              aria-label="show"
              icon={isMasked ? 'Eye16' : 'EyeClosed16'}
              onClick={onToggleisMasked}
              type="button"
            />
          }
          innerRef={setInputRef}
          maxLength={11}
          onBlur={onBlur}
          onChange={onChange}
          // onKeyDown occurs before onChange, so we unmask and update internal value before onChange fires
          onKeyDown={() => {
            if (currentlyMaskedValue != null) {
              setDisplayValue(currentlyMaskedValue)
              setCurrentlyMaskedValue(null)
            }
          }}
          overrideInputStyles={{
            fontFeatureSettings: 'tnum',
          }}
          placeholder="XXX-XX-XXXX"
          state={state}
          type="text"
          value={currentlyMaskedValue ? maskSSN(displayValue) : displayValue}
          {...inputProps}
        />
      )}
    </InternalInputWrapper>
  )
}
